import { createEnvironmentInjector, effect, EnvironmentInjector, inject, Injector, Provider, signal, Signal, Type, untracked } from '@angular/core';

import { AsyncRegistry } from '../registry/registry.types';
import { ConnectServiceOptions, ResolvedService, ResolvedServiceRef } from './services-registry.types';

export class ServicesRegistry<TService> {
  /** Exposed so a nested ServicesRegistry can chain its own registry onto this one. */
  readonly registry: AsyncRegistry<Type<TService>>;
  readonly #fallbackInjector: EnvironmentInjector;

  constructor(registry: AsyncRegistry<Type<TService>>, fallbackInjector: EnvironmentInjector) {
    this.registry = registry;
    this.#fallbackInjector = fallbackInjector;
  }

  connect(key: Signal<string | null | undefined>, options?: ConnectServiceOptions): ResolvedServiceRef<TService> {
    const ownerInjector = options?.injector ?? inject(Injector);
    const parentInjector = ownerInjector.get(EnvironmentInjector);

    const instance = signal<TService | null>(null);
    const isLoading = signal(false);
    const error = signal<Error | undefined>(undefined);

    effect(
      (onCleanup) => {
        const entryKey = key();

        instance.set(null);
        error.set(undefined);

        if (!entryKey) {
          isLoading.set(false);
          return;
        }

        let resolved: ResolvedService<TService> | null = null;
        let isStale = false;

        onCleanup(() => {
          isStale = true;
          resolved?.destroy();
        });

        isLoading.set(true);

        this.#resolve(entryKey, parentInjector, options?.preset, options?.providers)
          .then((service) => {
            if (isStale) {
              service.destroy();
              return;
            }

            resolved = service;
            instance.set(service.instance);
          })
          .catch((reason) => {
            if (!isStale) {
              error.set(reason instanceof Error ? reason : new Error(String(reason)));
            }
          })
          .finally(() => {
            if (!isStale) {
              isLoading.set(false);
            }
          });
      },
      { injector: ownerInjector }
    );

    return {
      instance: instance.asReadonly(),
      isLoading: isLoading.asReadonly(),
      error: error.asReadonly(),
    };
  }

  async #resolve(key: string, parentInjector?: EnvironmentInjector, preset?: string, providers?: Provider[]): Promise<ResolvedService<TService>> {
    const serviceLoader = untracked(() => this.registry.getEntry(key, preset)());
    const serviceType = await serviceLoader();

    const serviceInjector = createEnvironmentInjector([serviceType, ...(providers ?? [])], parentInjector ?? this.#fallbackInjector);
    const instance = untracked(() => serviceInjector.get(serviceType));

    let isDestroyed = false;

    return {
      instance,
      destroy: () => {
        if (isDestroyed) {
          return;
        }

        isDestroyed = true;
        serviceInjector.destroy();
      },
    };
  }
}
